import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { formatTimeAgo } from '../utils/helpers';
import toast from 'react-hot-toast';
import { Monitor, Plus, RefreshCw, Pencil, Trash2, Play, X, ListVideo } from 'lucide-react';

export default function DevicesPage() {
  const [devices, setDevices] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDevice, setEditDevice] = useState(null);
  const [assignDevice, setAssignDevice] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);

  const fetchData = async () => {
    try {
      const [devRes, plRes, grRes] = await Promise.all([
        api.get('/devices'),
        api.get('/playlists'),
        api.get('/device-groups'),
      ]);
      setDevices(devRes.data.devices || []);
      setPlaylists(plRes.data.playlists || []);
      setGroups(grRes.data.groups || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 10000); return () => clearInterval(i); }, []);

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/devices/${editDevice.id}`, {
        name: editDevice.name,
        tv_number: editDevice.tv_number,
        group_id: editDevice.group_id || null,
      });
      toast.success('Device updated');
      setEditDevice(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const handleAssign = async (playlistId) => {
    try {
      if (selectedDevices.length > 0) {
        await api.post('/devices/bulk-assign', {
          device_ids: selectedDevices,
          playlist_id: playlistId,
        });
        toast.success(`Playlist assigned to ${selectedDevices.length} devices`);
        setSelectedDevices([]);
      } else if (assignDevice) {
        await api.post(`/devices/${assignDevice.id}/assign-playlist`, {
          playlist_id: playlistId,
        });
        toast.success('Playlist assigned');
      }
      setAssignDevice(null);
      fetchData();
    } catch (err) { toast.error('Failed to assign'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this device?')) return;
    try {
      await api.delete(`/devices/${id}`);
      toast.success('Device deleted');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const handleCommand = async (id, type) => {
    try {
      await api.post(`/devices/${id}/command`, { command_type: type });
      toast.success(`Command '${type}' sent`);
    } catch (err) { toast.error('Failed'); }
  };

  const toggleSelect = (id) => {
    setSelectedDevices(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  if (loading) return <div className="loading-page"><span className="spinner" /></div>;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h2>TV Devices</h2>
          <div className="page-header-sub">{devices.length} devices registered</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selectedDevices.length > 0 && (
            <button className="btn btn-primary btn-sm" onClick={() => setAssignDevice({ bulk: true })}>
              <Play size={14} /> Assign to {selectedDevices.length} TVs
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={fetchData}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {devices.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <Monitor size={48} />
            <h3>No devices yet</h3>
            <p>Install the IMA TV app on your Xiaomi TVs. They will appear here once connected.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input type="checkbox"
                      checked={selectedDevices.length === devices.length && devices.length > 0}
                      onChange={() => setSelectedDevices(
                        selectedDevices.length === devices.length ? [] : devices.map(d => d.id)
                      )}
                    />
                  </th>
                  <th>TV</th>
                  <th>Status</th>
                  <th>Playlist</th>
                  <th>Group</th>
                  <th>Last Seen</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <input type="checkbox"
                        checked={selectedDevices.includes(d.id)}
                        onChange={() => toggleSelect(d.id)}
                      />
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{d.tv_number || 'Unassigned'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
                        {d.name || d.device_uid?.slice(0, 12)}
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-${d.effective_status || d.status}`}>
                        <span className="badge-dot" />
                        {d.effective_status || d.status}
                      </span>
                    </td>
                    <td style={{ color: d.playlist_name ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                      {d.playlist_name || 'None'}
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{d.group_name || '—'}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-tertiary)' }}>
                      {formatTimeAgo(d.last_heartbeat)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-ghost btn-icon btn-sm" title="Edit"
                          onClick={() => setEditDevice({ ...d })}>
                          <Pencil size={14} />
                        </button>
                        <button className="btn btn-ghost btn-icon btn-sm" title="Assign Playlist"
                          onClick={() => setAssignDevice(d)}>
                          <Play size={14} />
                        </button>
                        <button className="btn btn-ghost btn-icon btn-sm" title="Restart"
                          onClick={() => handleCommand(d.id, 'restart')}>
                          <RefreshCw size={14} />
                        </button>
                        <button className="btn btn-ghost btn-icon btn-sm" title="Delete"
                          style={{ color: 'var(--danger)' }}
                          onClick={() => handleDelete(d.id)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editDevice && (
        <div className="modal-overlay" onClick={() => setEditDevice(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Device</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setEditDevice(null)}><X size={18} /></button>
            </div>
            <form onSubmit={handleEdit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">TV Number</label>
                  <input className="form-input" value={editDevice.tv_number || ''}
                    onChange={e => setEditDevice({ ...editDevice, tv_number: e.target.value })}
                    placeholder="TV-001" />
                </div>
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input className="form-input" value={editDevice.name || ''}
                    onChange={e => setEditDevice({ ...editDevice, name: e.target.value })}
                    placeholder="Lobby TV" />
                </div>
                <div className="form-group">
                  <label className="form-label">Group</label>
                  <select className="form-input" value={editDevice.group_id || ''}
                    onChange={e => setEditDevice({ ...editDevice, group_id: e.target.value })}>
                    <option value="">No group</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
                  UID: {editDevice.device_uid}<br />
                  IP: {editDevice.last_ip || 'Unknown'}<br />
                  App: {editDevice.app_version || 'Unknown'}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditDevice(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Playlist Modal */}
      {assignDevice && (
        <div className="modal-overlay" onClick={() => { setAssignDevice(null); setSelectedDevices([]); }}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Assign Playlist</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setAssignDevice(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                {assignDevice.bulk
                  ? `Assign playlist to ${selectedDevices.length} selected TVs`
                  : `Assign playlist to ${assignDevice.tv_number || assignDevice.name || 'device'}`
                }
              </p>
              {playlists.length === 0 ? (
                <p style={{ color: 'var(--text-tertiary)' }}>No playlists created yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {playlists.map(p => (
                    <button key={p.id} className="btn btn-secondary"
                      style={{ justifyContent: 'flex-start' }}
                      onClick={() => handleAssign(p.id)}>
                      <ListVideo size={16} /> {p.name}
                      <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {p.item_count} items
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
